import { toast } from 'material-react-toastify';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import { BasketItem } from '../../interfaces/Basket/BasketItem';
import { updateBasketItemQuantity } from '../../utils/Basket';
import { useThemeColor } from '../../hooks/useThemeColor';
import { BasketAmountInput } from './BasketAmountInput';
import { sharedColors } from '../../utils/Style';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type BasketCardProps = {
  item: BasketItem;
  onUpdate: () => void;
};

export const BasketCard = ({ item, onUpdate }: BasketCardProps) => {
  const t = useLocalizedText();

  const { primary, secondary } = useThemeColor();

  const handleDelete = () => {
    updateBasketItemQuantity(item, 0)
      .then(onUpdate)
      .catch((err) => toast.error(err.message));
  };

  const handleChangeQuantity = (newQuantity: number) => {
    updateBasketItemQuantity(item, newQuantity)
      .then(onUpdate)
      .catch((err) => toast.error(err.message));
  };

  return (
    <Link to={`/urun/${item.product.slug}`} style={{ textDecoration: 'none' }}>
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'center',
          width: '100%',
          p: 2.5,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <IconButton
          size='small'
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            handleDelete();
          }}
          sx={{
            backgroundColor: primary,
            color: secondary,
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          component='img'
          src={item.product.baseImage.path}
          height={160}
          width='auto'
        />

        <Typography sx={{ fontWeight: 600, textAlign: 'center' }}>
          {item.product.name}
        </Typography>

        {!!item.options.length && (
          <Typography
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              fontSize: '14px',
              color: sharedColors.gray5,
              mt: -1,
            }}
          >
            {item.options[0].item.label}
          </Typography>
        )}

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, width: '30%', textAlign: 'right' }}
          >
            {t('basket.quantity')}:
          </Typography>

          <BasketAmountInput
            basketItem={item}
            onChange={handleChangeQuantity}
          />
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, width: '30%', textAlign: 'right' }}
          >
            {t('basket.total')}:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {item.total.amount !== item.price.amount && (
              <Typography
                sx={{
                  color: sharedColors.statusRed,
                  textDecoration: 'line-through',
                }}
              >
                {item.price.formatted}
              </Typography>
            )}
            <Typography>{item.total.formatted}</Typography>

            {item.hasOfferApplied && <LocalOfferIcon sx={{ color: primary }} />}
            {item.hasAtCheckoutDiscount && (
              <OfflineBoltIcon sx={{ color: primary }} />
            )}
          </Box>
        </Box>
      </Paper>
    </Link>
  );
};
